import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  ClockIcon,
  CpuChipIcon,
  ChartPieIcon,
  PresentationChartBarIcon,
  TableCellsIcon,
  DocumentChartBarIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from '../utils/notify';

const AiAnalytics = () => {
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [reportType, setReportType] = useState('compliance');

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchReports();
    fetchAnalytics();
  }, []);

const fetchReports = async () => {
  try {
    setLoading(true);
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_BASE}/api/reports/`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    // Ensure we always have an array
    const data = Array.isArray(response.data) ? response.data : response.data.results || [];
    setReports(data);

  } catch (error) {
    console.error('Error fetching reports:', error);
    toast.error('Failed to load reports');
  } finally {
    setLoading(false);
  }
};

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/dashboard/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const generateReport = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE}/api/reports/generate/`, {
        type: type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Report generated successfully');
      setShowGenerateModal(false);
      fetchReports();
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    }
  };

  const downloadReport = async (reportId, title) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/reports/${reportId}/download/`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <CpuChipIcon className="w-8 h-8 mr-3" style={{ color: "var(--app-primary-color, #26256b)" }} />
                AI Analytics
              </h1>
              <p className="text-gray-600 mt-1">Generate cross-module reports with AI-powered insights aligned with PowerBI, Splunk, and Tableau</p>
            </div>
            <button
              onClick={() => setShowGenerateModal(true)}
              className="text-white px-4 py-2 rounded-lg transition-opacity hover:opacity-90 flex items-center"
              style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Generate Report
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Analytics Dashboard */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-3 rounded-full" style={{ backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 15%, transparent)" }}>
                  <ChartBarIcon className="w-6 h-6" style={{ color: "var(--app-primary-color, #26256b)" }} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Compliance Rate</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.compliance?.compliance_rate?.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 rounded-full">
                  <ChartBarIcon className="w-6 h-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Risk Score</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.risk?.risk_score?.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-full">
                  <ChartBarIcon className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Frameworks</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.compliance?.frameworks || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-full">
                  <ChartBarIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Vendors</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.vendors?.total_vendors || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reports List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Generated Reports</h2>
          </div>
          
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: "var(--app-primary-color, #26256b)" }}></div>
              </div>
            ) : (
              <div className="space-y-4">
                {reports.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <DocumentTextIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No reports generated yet</p>
                    <button
                      onClick={() => setShowGenerateModal(true)}
                      className="mt-2 hover:opacity-80"
                      style={{ color: "var(--app-primary-color, #26256b)" }}
                    >
                      Generate your first report
                    </button>
                  </div>
                ) : (
                  reports.map((report) => (
                    <motion.div
                      key={report.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-2" />
                            <h3 className="font-semibold text-gray-900">{report.title}</h3>
                            <span className="ml-3 px-2 py-1 text-xs rounded-full text-white" style={{ backgroundColor: "var(--app-primary-color, #26256b)", opacity: 0.85 }}>
                              {report.type}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <ClockIcon className="w-4 h-4 mr-1" />
                            <span>Generated on {new Date(report.generated_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => downloadReport(report.id, report.title)}
                          className="ml-4 px-4 py-2 text-white rounded-lg transition-opacity hover:opacity-90 flex items-center"
                          style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
                        >
                          <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                          Download
                        </button>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Generate Report Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Report</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report Type
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'cross-module', label: 'Cross-Module Analytics Report', desc: 'Comprehensive insights across all GRC modules' },
                      { value: 'compliance-status', label: 'Compliance Status Report', desc: 'Framework and control status analysis' },
                      { value: 'risk-heatmap', label: 'Risk Heatmap & Dashboard', desc: 'Visual risk assessment and heat mapping' },
                      { value: 'cloud-posture', label: 'Cloud Security Posture Report', desc: 'Cloud infrastructure security analysis' },
                      { value: 'audit-log', label: 'Audit Log Reporting', desc: 'Comprehensive audit trail analysis' },
                      { value: 'custom-export', label: 'Custom Export (PDF, XLSX, CSV)', desc: 'Flexible format export options' }
                    ].map((type) => (
                      <label key={type.value} className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          value={type.value}
                          checked={reportType === type.value}
                          onChange={(e) => setReportType(e.target.value)}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{type.label}</div>
                          <div className="text-sm text-gray-600">{type.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => generateReport(reportType)}
                className="px-4 py-2 text-white rounded-md transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiAnalytics;